import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { can, isOutsider } from '../../utils/permissions';
import { isValidCoordinate } from '../../utils/geo';
import {
  ALL_VIEWS, bestPhotoFix, codeFixed, coverPhoto, detailsForAiAnswer, isAiPickedName, leaseActive, newBatch,
  newBatchAudio, newBatchPhoto, nextView,
} from '../../utils/fieldBatches';
import { EDIT_HOLD_MS } from '../../utils/fieldOutbox';
import { exifFromFile } from '../../utils/exifGps';
import { thumbnailOf } from '../../utils/fieldFrame';
import { fromLocalInput, localIsoWithOffset, toLocalInput } from '../../utils/specimenCode';
import MapGpsPicker from '../MapGpsPicker';
import PhotoThumb from '../PhotoThumb';
import FieldScreen from './FieldScreen';
import FieldDetailsForm from './FieldDetailsForm';
import FieldSentEditor from './FieldSentEditor';
import AiFieldSuggestion from './AiFieldSuggestion';
import api from '../../utils/api';
import { tagBlocks, useTagCheck } from './useTagCheck';
import { useBackLabels, useBackToGallery } from './useFieldNav';
import { attentionText, pageStyles as ps } from './fieldFormat';
import { isReadOnlyRefusal } from '../../utils/viewAs';
import { newBatchThermal, thermalContentType } from '../../utils/thermal/upload';
import FieldThermalImport from '../thermal/FieldThermalImport';
import { keepAnnotationsFor } from '../../utils/fieldAnnotations';
import { mayRecordAudio } from '../../utils/audioNotes';

/** One batch of this person's, as the store has it now, kept current. */
function useLiveBatch(store, userId, find) {
  const [live, setLive] = useState({ state: 'loading', batch: null });
  const finder = useRef(find);
  finder.current = find;
  useEffect(() => {
    let alive = true;
    const load = () => store.list(userId)
      .then((all) => {
        if (!alive) return;
        const batch = all.find(b => b.status !== 'active' && finder.current(b)) || null;
        setLive({ state: 'ready', batch });
      })
      .catch(() => { if (alive) setLive({ state: 'ready', batch: null }); });
    load();
    const off = store.subscribe(load);
    return () => { alive = false; off(); };
  }, [store, userId]);
  return live;
}

/**
 * Field Mode's editor, in three modes:
 *
 *   - local: a batch on this phone that has not reached the server.
 *     Everything can change: when, where, the photos and the details -- save
 *     what makes up a code already minted for it. Saved, it goes back to the
 *     outbox.
 *   - server: a collection the server has (from the gallery, or a batch that
 *     got there). Its code, date and place are part of its permanent
 *     specimen code and are shown, not edited; see FieldSentEditor.
 *   - retro: a new specimen found earlier, from photos already on the phone,
 *     dated and placed by hand -- or by its photos, where their EXIF says
 *     when and where they were taken, for the collector to check.
 *
 * A photo added from the phone's storage carries what its EXIF says of when
 * and where it was taken (utils/exifGps.js) as its own tags, as a photo from
 * the camera does.
 */
export default function FieldBatchEditor({ mode, store, outbox, userId, user }) {
  const { batchId, sampleId } = useParams();
  const { t } = useTranslation('field');
  const back = useBackToGallery();
  const backLabels = useBackLabels();
  // Unsaved changes in the form below: a batch the outbox takes meanwhile
  // does not swap them out from under the collector.
  const [dirty, setDirty] = useState(false);
  const live = useLiveBatch(store, userId, mode === 'server'
    ? b => String(b.serverSampleId) === String(sampleId)
    : b => b.id === batchId);

  if (mode === 'retro') {
    return <DraftEditor retro store={store} outbox={outbox} userId={userId} user={user} />;
  }
  if (mode === 'server') {
    if (live.state === 'loading') return null;
    return (
      <FieldSentEditor sampleId={sampleId} batch={live.batch} store={store} outbox={outbox} userId={userId}
        user={user} />
    );
  }
  if (live.state === 'loading') return null;
  if (!live.batch) {
    return (
      <FieldScreen testid="field-editor" title={t('editor.title')} backLabel={t(backLabels[0])}
        backTip={t(backLabels[1])} onBack={back}>
        <p style={ps.note} data-testid="editor-missing">{t('editor.missing')}</p>
      </FieldScreen>
    );
  }
  if (live.batch.serverSampleId && !dirty) {
    return (
      <FieldSentEditor sampleId={live.batch.serverSampleId} batch={live.batch} store={store} outbox={outbox}
        userId={userId} user={user} />
    );
  }
  return (
    <DraftEditor key={live.batch.id} batch={live.batch} store={store} outbox={outbox} userId={userId} user={user}
      onDirty={setDirty} takenMeanwhile={!!live.batch.serverSampleId} />
  );
}

const sameSpot = (a, b) => !!a && !!b && Number(a.lat) === Number(b.lat) && Number(a.lng) === Number(b.lng);
const tagFromAttention = (batch) => {
  const a = batch?.attention;
  if (a?.reason !== 'label') return { status: 'idle' };
  return { status: a.code === 'code_reserved' ? 'reserved' : 'inUse', code: a.label };
};
const formOf = state => JSON.stringify(state);

/**
 * A specimen not yet on the server: a camera batch to correct, or a new
 * retrospective one. Save writes it to the device; the outbox sends it.
 *
 * While it is open, its batch is held back from the outbox (holdUntil), so a
 * label or "only a sighting" given here goes before a code is minted. Once a
 * code has been minted for it (or a mint asked for one), the date, the place,
 * the label and "only a sighting" no longer change here: the record has to
 * say what that code says. A correction of those is made, with a reason, on
 * the specimen's page.
 */
function DraftEditor({ batch, retro = false, store, outbox, userId, user, onDirty, takenMeanwhile = false }) {
  const { t } = useTranslation(['field', 'viewAs', 'ai']);
  const back = useBackToGallery();
  const backLabels = useBackLabels();
  const canMint = can(user, 'specimen:register');
  const fixed = !retro && codeFixed(batch);
  const [when, setWhen] = useState(() => toLocalInput(batch?.startedAt));
  const [lat, setLat] = useState(() => (batch?.location ? String(batch.location.lat) : ''));
  const [lng, setLng] = useState(() => (batch?.location ? String(batch.location.lng) : ''));
  const [photos, setPhotos] = useState(() => (batch?.photos || []).filter(p => !p.skipped));
  const [details, setDetails] = useState(() => ({ ...(batch?.details || {}) }));
  // Voice notes not yet on the server, with their recordings (read from the
  // device below; a new one carries its own).
  const [audio, setAudio] = useState(() => (batch?.audioNotes || []).filter(a => !a.sent && !a.skipped)
    .map(a => ({ ...a, blob: null })));
  // Thermal files not yet on the server (the Thermal step): those already on
  // the device by id, one picked in this edit with its File, kept until Save.
  const canThermal = can(user, 'lab:observe');
  const [thermal, setThermal] = useState(() => (batch?.thermal || []).filter(f => !f.sent && !f.skipped));
  const thermalFiles = useRef({});
  const [previews, setPreviews] = useState({});
  // The photos' files as the device keeps them, for "Identify with AI".
  const [blobs, setBlobs] = useState({});
  // The AI's answer kept with the batch ({aiId, accepted, ...}), linked to the
  // record by the outbox once it is made; and that answer in full, read
  // back from the server to show its candidates again.
  const [ai, setAi] = useState(() => batch?.ai || null);
  const aiRef = useRef(ai);
  // A new answer, another pick or "none" moves a name taken from the AI with
  // it (the summary's rule, fieldBatches.detailsForAiAnswer): a name on the
  // record from the AI always goes with its answer's `accept`.
  const answerChanged = (meta) => {
    const before = aiRef.current;
    aiRef.current = meta;
    setDetails(d => detailsForAiAnswer(d, before, meta));
    setAi(meta);
  };
  const [latest, setLatest] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  // Picked files whose EXIF is still being read: Save waits for them, or they
  // would be left out of a save tapped meanwhile.
  const [reading, setReading] = useState(0);
  // A retrospective date or place filled in from the photos' EXIF.
  const [fromPhotos, setFromPhotos] = useState(false);
  const tag = useTagCheck(tagFromAttention(batch), outbox.signedIn);
  // Photos added in this edit, not yet on the device: id -> File.
  const files = useRef({});
  // The form as it is now, for files whose EXIF is read meanwhile; and the
  // photo fix it was placed from, whose accuracy goes with that place.
  const formNow = useRef({ when, lat, lng });
  formNow.current = { when, lat, lng };
  const photoFix = useRef(null);
  const urls = useRef({});
  const removed = useRef([]);
  const fileInput = useRef(null);
  const audioIds = audio.map(a => a.id);
  const thermalIds = thermal.map(f => f.id);
  const initial = useRef(formOf({ when, lat, lng, photos, details, audioIds, ai, thermalIds }));

  const dirty = initial.current !== formOf({ when, lat, lng, photos, details, audioIds, ai, thermalIds });
  useEffect(() => { onDirty?.(dirty); }, [dirty, onDirty]);
  useEffect(() => () => onDirty?.(false), [onDirty]);

  // Held back from the outbox while it is open; let go when it closes.
  useEffect(() => {
    if (retro || !batch?.id) return undefined;
    const id = batch.id;
    let held = false;
    store.update(userId, id, (current) => {
      if (current.status !== 'pending' || current.serverSampleId) return null;
      held = true;
      return { ...current, holdUntil: new Date(Date.now() + EDIT_HOLD_MS).toISOString() };
    }).catch(() => {});
    return () => {
      if (!held) return;
      store.update(userId, id, current => (current.holdUntil ? { ...current, holdUntil: undefined } : null))
        .catch(() => {})
        .finally(() => outbox.run());
    };
  }, [batch?.id, retro, store, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const preview = (id, blob) => {
    if (!blob || typeof URL.createObjectURL !== 'function') return;
    urls.current[id] = URL.createObjectURL(blob);
    setPreviews({ ...urls.current });
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      for (const p of batch?.photos || []) {
        const blob = await store.getBlob(userId, p.id);
        if (alive) preview(p.id, blob);
        if (alive && blob) setBlobs(b => ({ ...b, [p.id]: blob }));
      }
      // The voice notes' recordings, to play back.
      for (const a of (batch?.audioNotes || []).filter(x => !x.sent && !x.skipped)) {
        const blob = await store.getBlob(userId, a.id).catch(() => null);
        if (alive && blob) setAudio(list => list.map(x => (x.id === a.id ? { ...x, blob } : x)));
      }
    })();
    return () => {
      alive = false;
      Object.values(urls.current).forEach(u => URL.revokeObjectURL(u));
      urls.current = {};
    };
  }, [batch?.id, store, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const addFiles = async (event) => {
    const chosen = [...(event.target.files || [])];
    event.target.value = '';
    if (!chosen.length) return;
    // When and where each file says it was taken, if it says.
    setReading(n => n + 1);
    let metas;
    try {
      metas = await Promise.all(chosen.map(f => exifFromFile(f).catch(() => null)));
    } finally {
      setReading(n => n - 1);
    }
    const fresh = chosen.map((file, i) => ({
      file,
      photo: newBatchPhoto({ view: 'other', takenAt: metas[i]?.takenAt || null, fix: metas[i]?.gps || null }),
    }));
    for (const { file, photo } of fresh) {
      files.current[photo.id] = file;
      preview(photo.id, file);
    }
    // Top, bottom, habitat, then extras, after whatever the batch has by now.
    setPhotos(list => fresh.reduce((next, { photo }) => {
      const view = nextView(next);
      return [...next, {
        ...photo, view, ...(view === 'other' ? { note: t('camera.extraNote', { n: next.length + 1 }) } : {}),
      }];
    }, list));
    if (!retro) return;
    // A retrospective specimen not yet dated or placed is dated by the
    // earliest of these photos and placed by the surest; what the collector
    // has entered is never written over, and a time still ahead is left out.
    const form = formNow.current;
    let filled = false;
    const earliest = fresh.map(f => f.photo.takenAt).filter(Boolean)
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0];
    if (!String(form.when).trim() && earliest && new Date(earliest).getTime() <= Date.now()) {
      setWhen(toLocalInput(earliest));
      filled = true;
    }
    const fix = bestPhotoFix(fresh.map(f => f.photo));
    if (String(form.lat).trim() === '' && String(form.lng).trim() === '' && fix) {
      photoFix.current = fix;
      setLat(String(fix.lat));
      setLng(String(fix.lng));
      filled = true;
    }
    if (filled) setFromPhotos(true);
  };

  // The answer kept with the batch, read back to show it again (online only;
  // offline the kept line says what was answered).
  const keptAiId = batch?.ai?.aiId;
  useEffect(() => {
    if (!keptAiId || !outbox.signedIn() || (typeof navigator !== 'undefined' && navigator.onLine === false)) {
      return undefined;
    }
    let alive = true;
    api.get(`/ai/identify/${keptAiId}`)
      .then((r) => { if (alive && r?.data?.ai_id) setLatest({ ...r.data, mine: true }); })
      .catch(() => {});
    return () => { alive = false; };
  }, [keptAiId]); // eslint-disable-line react-hooks/exhaustive-deps

  // The photos as the viewer opens them full size (PhotoThumb).
  const viewable = photos.filter(p => previews[p.id])
    .map(p => ({ s3_url: previews[p.id], photo_type: p.view, photo_id: p.id, notes: p.note }));

  // The photographs the AI may look at: this edit's files and the device's.
  const aiPhotos = photos.map(p => ({ view: p.view, file: files.current[p.id] || blobs[p.id] || null }))
    .filter(p => p.file);

  const setView = (id, view) => setPhotos(list => list.map((p, i) => (p.id !== id ? p : {
    ...p, view, note: view === 'other' && !p.note ? t('camera.extraNote', { n: i + 1 }) : p.note,
  })));

  const remove = (id) => {
    if (files.current[id]) delete files.current[id];
    else removed.current.push(id);
    if (urls.current[id]) URL.revokeObjectURL(urls.current[id]);
    delete urls.current[id];
    setPreviews({ ...urls.current });
    setPhotos(list => list.filter(p => p.id !== id));
  };

  const status = batch?.status;
  // Being sent by somebody right now; a claim left by a tab that closed has
  // run out, and the batch can be corrected.
  const busy = status === 'sending' && leaseActive(batch);
  const labelBlocked = tagBlocks(tag.tagState, details.label);
  const hasCoords = String(lat).trim() !== '' || String(lng).trim() !== '';

  const save = async () => {
    setError('');
    const moment = when ? new Date(when) : null;
    if (!moment || Number.isNaN(moment.getTime())) return setError(t('editor.whenRequired'));
    if (moment.getTime() > Date.now() + 60 * 1000) return setError(t('editor.whenFuture'));
    if ((retro || hasCoords) && !isValidCoordinate(lat, lng)) {
      return setError(t(retro && !hasCoords ? 'editor.whereRequired' : 'editor.whereInvalid'));
    }
    setSaving(true);
    const added = Object.keys(files.current);
    // Voice notes recorded in this edit go to the device with the photos; one
    // deleted here goes from it once the batch no longer names it.
    const known = new Set((batch?.audioNotes || []).map(a => a.id));
    const newAudio = audio.filter(a => !known.has(a.id) && a.blob);
    const keptAudio = new Set(audio.map(a => a.id));
    const droppedAudio = (batch?.audioNotes || []).filter(a => !a.sent && !a.skipped && !keptAudio.has(a.id))
      .map(a => a.id);
    const audioNotesOf = current => [
      ...(current?.audioNotes || []).filter(a => a.sent || a.skipped || keptAudio.has(a.id)),
      ...newAudio.map(a => newBatchAudio({
        id: a.id, contentType: a.contentType, durationS: a.durationS, recordedAt: a.recordedAt,
      })),
    ];
    // The thermal files the same way: a new one's bytes go to the device as
    // they were picked; one removed here goes once the batch no longer names it.
    const knownThermal = new Set((batch?.thermal || []).map(f => f.id));
    const newThermal = thermal.filter(f => !knownThermal.has(f.id) && thermalFiles.current[f.id]);
    const keptThermal = new Set(thermal.map(f => f.id));
    const droppedThermal = (batch?.thermal || []).filter(f => !f.sent && !f.skipped && !keptThermal.has(f.id))
      .map(f => f.id);
    const thermalOf = current => [
      ...(current?.thermal || []).filter(f => f.sent || f.skipped || keptThermal.has(f.id)),
      ...newThermal,
    ];
    try {
      for (const id of added) await store.putBlob(userId, id, files.current[id]);
      for (const a of newAudio) await store.putBlob(userId, a.id, a.blob);
      for (const f of newThermal) await store.putBlob(userId, f.id, thermalFiles.current[f.id]);
      // A place kept as it was (or as a photo gave it) keeps its accuracy.
      const known = [batch?.location, photoFix.current].find(l => sameSpot(l, { lat, lng }));
      const location = hasCoords
        ? { lat: Number(lat), lng: Number(lng), accuracy_m: known?.accuracy_m ?? null }
        : null;
      const startedAt = toLocalInput(batch?.startedAt) === when ? batch.startedAt : fromLocalInput(when);
      const cover = coverPhoto({ photos });
      const coverChanged = !batch || coverPhoto(batch)?.id !== cover?.id;
      let thumbnail = batch?.thumbnail || null;
      if (coverChanged) {
        const blob = cover ? (files.current[cover.id] || await store.getBlob(userId, cover.id)) : null;
        thumbnail = blob ? await thumbnailOf(blob) : null;
      }

      if (retro) {
        await store.save({
          ...newBatch({ userId, source: 'retro' }),
          status: 'pending', startedAt, location, photos, details, thumbnail,
          ...(ai?.aiId ? { ai } : {}),
          audioNotes: audioNotesOf(null),
          thermal: thermalOf(null),
          finalizedAt: new Date().toISOString(),
        });
      } else {
        let conflict = false;
        await store.update(userId, batch.id, (current) => {
          // The outbox took it meanwhile: its record may already be made, and
          // writing the form over it would undo what it saved.
          if ((current.status === 'sending' && leaseActive(current)) || current.serverSampleId) {
            conflict = true;
            return null;
          }
          // A code minted meanwhile settles when, where and what it carries:
          // the form's copies of those are not written over it.
          const settled = codeFixed(current);
          return {
            ...current,
            ...(settled ? {} : {
              startedAt,
              location,
              // A new place has its own name; the outbox looks it up.
              ...(sameSpot(current.location, location) ? {} : { region: undefined }),
            }),
            photos,
            // A photo removed here takes its marks with it (the Pro drawer's
            // and the summary's outline): nothing is sent for it.
            ...keepAnnotationsFor(current, photos.map(p => p.id)),
            audioNotes: audioNotesOf(current),
            thermal: thermalOf(current),
            // A new answer is linked afresh once the record exists.
            ai: ai || current.ai,
            aiLinked: ai?.aiId && ai.aiId !== current.ai?.aiId ? false : current.aiLinked,
            details: settled
              ? { ...details, label: current.details?.label, observeOnly: current.details?.observeOnly }
              : details,
            thumbnail,
            status: 'pending',
            attention: null,
            holdUntil: undefined,
            photoPasses: 0,
            serverErrors: 0,
          };
        });
        if (conflict) {
          await store.removeBlobs(userId, [...added, ...newAudio.map(a => a.id), ...newThermal.map(f => f.id)]);
          setSaving(false);
          return setError(t('editor.busy'));
        }
        await store.removeBlobs(userId, [...removed.current, ...droppedAudio, ...droppedThermal]);
      }
      files.current = {};
      thermalFiles.current = {};
      initial.current = formOf({ when, lat, lng, photos, details, audioIds, ai, thermalIds });
      onDirty?.(false);
      store.ensureDurable?.().catch(() => {});
      outbox.run();
      // Back to the gallery it was opened from: a Back after this must not
      // return to the form just saved.
      back();
    } catch (e) {
      setSaving(false);
      setError(isReadOnlyRefusal(e) ? t('viewAs:notKept') : t('editor.storeFailed'));
    }
    return undefined;
  };

  const reason = batch?.attention?.reason;
  const nowInput = toLocalInput(localIsoWithOffset(new Date()));

  return (
    <FieldScreen
      testid="field-editor"
      title={retro ? t('retro.title') : t('editor.title')}
      backLabel={t(backLabels[0])}
      backTip={t(backLabels[1])}
      onBack={back}
    >
      <div data-testid="editor-mode" data-mode={retro ? 'retro' : 'local'} hidden />
      {retro && <p style={ps.hint}>{t('retro.help')}</p>}
      {takenMeanwhile && (
        <div style={ps.warn} role="alert" data-testid="editor-taken">
          <p style={{ margin: 0 }}>{t('editor.takenMeanwhile')}</p>
          <button type="button" style={{ ...ps.linkBtn, paddingInline: 0 }} title={t('tip.editor.discard')}
            data-testid="editor-discard" onClick={() => onDirty?.(false)}>
            {t('editor.discardAndOpen')}
          </button>
        </div>
      )}
      {!retro && status === 'attention' && (
        <div style={ps.warn} role="alert" data-testid="editor-attention" data-reason={reason}>
          <p style={{ margin: 0 }}>{attentionText(t, batch.attention)}</p>
          {batch.attention.message && (reason === 'refused' || reason === 'submit') && (
            <p style={{ ...ps.hint, marginTop: 6 }} data-testid="editor-attention-detail">
              <bdi dir="ltr">{batch.attention.message}</bdi>
            </p>
          )}
        </div>
      )}
      {!retro && status !== 'attention' && !takenMeanwhile && (
        <p style={ps.note} data-testid="editor-status">{t(busy ? 'editor.sendingNow' : 'editor.waiting')}</p>
      )}

      <fieldset style={ps.section} disabled={busy || saving || fixed || takenMeanwhile} data-testid="editor-when-where">
        <legend style={{ ...ps.h2, padding: 0 }}>{t('editor.whenWhere')}</legend>
        {fixed && (
          <p style={ps.hint} data-testid="editor-code-fixed">
            {t('editor.codeFixedHelp', { code: batch.specimenCode || t('gallery.codePending') })}
          </p>
        )}
        {fromPhotos && <p style={ps.hint} role="status" data-testid="editor-from-photos">{t('retro.fromPhotos')}</p>}
        <label style={ps.field}>
          <span style={ps.label}>{t('editor.when')}</span>
          <input type="datetime-local" style={ps.input} value={when} max={nowInput} required={retro}
            title={t('tip.editor.when')} data-testid="editor-when" onChange={e => setWhen(e.target.value)} />
        </label>
        <div data-testid="editor-where">
          <MapGpsPicker lat={lat} lng={lng} onChange={(a, b) => { if (!fixed) { setLat(a); setLng(b); } }} />
        </div>
      </fieldset>

      <fieldset style={ps.section} disabled={busy || saving || takenMeanwhile} data-testid="editor-photos">
        <legend style={{ ...ps.h2, padding: 0 }}>{t('editor.photos')}</legend>
        {photos.length === 0 && <p style={ps.hint}>{t('editor.noPhotos')}</p>}
        <ul style={es.photos}>
          {photos.map((p, i) => (
            <li key={p.id} style={es.photo} data-testid={`editor-photo-${i}`} data-view={p.view}>
              <span style={es.photoThumb}>
                {previews[p.id]
                  ? (
                    <PhotoThumb photos={viewable} index={viewable.findIndex(v => v.photo_id === p.id)}
                      style={{ width: '100%', height: '100%' }} testId={`editor-photo-open-${i}`}
                      label={t('camera.trayPhoto', { n: i + 1, view: t(`views.${p.view}`) })}>
                      <img src={previews[p.id]} alt={t('camera.trayPhoto', { n: i + 1, view: t(`views.${p.view}`) })}
                        style={es.photoImg} />
                    </PhotoThumb>
                  )
                  : <span aria-hidden="true">📷</span>}
              </span>
              <label style={{ ...ps.field, flex: 1, minWidth: 0 }}>
                <span style={ps.label}>{t('editor.view', { n: i + 1 })}</span>
                <select style={ps.input} value={p.view} title={t('tip.editor.view')}
                  data-testid={`editor-photo-view-${i}`} onChange={e => setView(p.id, e.target.value)}>
                  {ALL_VIEWS.map(v => <option key={v} value={v}>{t(`views.${v}`)}</option>)}
                </select>
              </label>
              <button type="button" style={ps.secondary} title={t('tip.editor.removePhoto')}
                aria-label={t('camera.removePhoto', { n: i + 1, view: t(`views.${p.view}`) })}
                data-testid={`editor-photo-remove-${i}`} onClick={() => remove(p.id)}>
                ×
              </button>
            </li>
          ))}
        </ul>
        <button type="button" style={{ ...ps.secondary, alignSelf: 'flex-start' }} title={t('tip.editor.addPhotos')}
          data-testid="editor-add-photos-button" onClick={() => fileInput.current?.click()}>
          {t('editor.addPhotos')}
        </button>
        {/* No `capture`: these are photos already on the phone. */}
        <input ref={fileInput} type="file" accept="image/*" multiple style={{ display: 'none' }} tabIndex={-1}
          aria-hidden="true" data-testid="editor-add-photos" onChange={addFiles} />
      </fieldset>

      {canThermal && (
        <div style={ps.section} data-testid="editor-thermal">
          <FieldThermalImport value={thermal} headingStyle={ps.h2}
            testid="editor-thermal-step" disabled={busy || saving || takenMeanwhile}
            onAdd={(file) => {
              const item = newBatchThermal({ name: file.name, size: file.size, contentType: thermalContentType(file),
                addedAt: localIsoWithOffset(new Date()) });
              thermalFiles.current[item.id] = file;
              setThermal(list => [...list, item]);
            }}
            onRemove={(id) => {
              delete thermalFiles.current[id];
              setThermal(list => list.filter(f => f.id !== id));
            }} />
        </div>
      )}

      <FieldDetailsForm value={details} onChange={setDetails} canMint={canMint} tag={tag}
        disabled={busy || saving || takenMeanwhile} codeFixed={fixed} canAsk={outbox.signedIn}
        audio={mayRecordAudio(user) ? { value: audio, onChange: setAudio } : null} showTag={!isOutsider(user)} />

      {(aiPhotos.length > 0 || latest) && (
        <section style={ps.section} aria-labelledby="draft-ai" data-testid="editor-draft-ai">
          <h2 id="draft-ai" style={ps.h2}>{t('ai:field.title')}</h2>
          <AiFieldSuggestion photos={aiPhotos} latest={latest} user={user} value={ai} onChange={answerChanged}
            hadOwnName={!!String(details.taxon_name || '').trim() && !isAiPickedName(details.taxon_name, ai)}
            disabled={busy || saving || takenMeanwhile}
            testid="editor-draft-ai-suggest"
            onAccept={chosen => setDetails(d => ({
              ...d, taxon_name: chosen.taxon_name, rank: chosen.rank, confidence: d.confidence || 'uncertain',
            }))} />
        </section>
      )}

      {error && <p style={ps.warn} role="alert" data-testid="editor-error">{error}</p>}
      <div style={ps.footer}>
        {labelBlocked && <p style={ps.hint} data-testid="editor-label-blocked">{t('details.labelBlocked')}</p>}
        <button type="button"
          style={{ ...ps.primary, width: '100%', opacity: busy || saving || reading || labelBlocked || takenMeanwhile ? 0.5 : 1 }}
          disabled={busy || saving || reading > 0 || labelBlocked || takenMeanwhile} title={t(retro ? 'tip.retro.save' : 'tip.editor.save')}
          data-testid="editor-save" onClick={save}>
          {saving ? t('editor.saving') : t(retro ? 'retro.save' : 'editor.save')}
        </button>
      </div>
    </FieldScreen>
  );
}

const es = {
  photos: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 },
  photo: { display: 'flex', alignItems: 'flex-end', gap: 10 },
  photoThumb: {
    flex: '0 0 auto', width: 64, height: 64, borderRadius: 8, overflow: 'hidden', background: 'var(--c-bg-subtle-6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  photoImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
};
